<template lang="pug">

TableTitle(title="Appointment Status",
  searchPlaceholder="Search by patient name or hospital"
  v-model="searchKeyword",
  @doSearch="fetchPatientAppointment",
  @blur="fetchPatientAppointment"
)
  template(#actions)
    DateSelector(@filterByDate="filterByDate")
.table.table--hoverable
  .th
    .td(@click.prevent="changeListOrder('DateAndTime', fetchPatientAppointment)" style="cursor: pointer;") Appt. Time
      i(style="font-weight: bolder;") {{ DateAndTimeColumnSortingIcon }}
    .td(@click.prevent="changeListOrder('PatientFullName', fetchPatientAppointment)" style="cursor: pointer;") Patient Name
      i(style="font-weight: bolder;") {{ PatientFullNameColumnSortingIcon }}
    .td(@click.prevent="changeListOrder('HospitalDoctorFullName', fetchPatientAppointment)" style="cursor: pointer;") Hospital Doctor
      i(style="font-weight: bolder;") {{ HospitalDoctorFullNameColumnSortingIcon }}
    .td(@click.prevent="changeListOrder('ClinicName', fetchPatientAppointment)" style="cursor: pointer;") Clinic
      i(style="font-weight: bolder;") {{ ClinicNameColumnSortingIcon }}
    .td(@click.prevent="changeListOrder('Status', fetchPatientAppointment)" style="cursor: pointer;") Last Activity
      i(style="font-weight: bolder;") {{ StatusColumnSortingIcon }}

    .td(@click.prevent="changeListOrder('Report', fetchPatientAppointment)" style="cursor: pointer;")  Report
      i(style="font-weight: bolder;") {{ ReportColumnSortingIcon }}

  .tr(
    v-for="appointment in appointments",
    :key="appointment.id",
    @click.prevent="openHospitalAppointmentRecordOverlay(appointment)"
  )
    .td
      div {{ getDateFromUnixFormatted(appointment.scheduledAt) }}
      .second-row-md-grey {{ getTimeFromUnixFormatted(appointment.scheduledAt)}}
    .td.bold {{ getUserFormatted(appointment.patient) }}
    .td {{ getDoctorFormatted(appointment.schedule.doctor) }}
    .td {{ appointment.clinic.name }}
    .td
      .green(v-if = "appointment.status == 'New' ") {{ appointment.status }}
      .green(v-if = "appointment.status == 'In Process' ") {{ appointment.status }}
      .green(v-if = "appointment.status == 'Completed' ") {{ appointment.status }}
      .green(v-if = "appointment.status == 'Updated' ") {{ appointment.status }}
      .red(v-if = "appointment.status == 'Rescheduled' ") {{ appointment.status }}
      .red(v-if = "appointment.status == 'Cancelled' ") {{ appointment.status }}
      
      .second-row-sm-grey Updated {{ getRelativeTimeFromUnixFormatted(appointment.updatedAt) }}
    .td
      base-icon(name="yes" v-if="appointment.idHospitalReport")
      base-icon(name="no" v-else)
      
Pagination(
  v-if="pagination && appointments != null && appointments.length",
  :pagination="pagination",
  :reloadFn="fetchPatientAppointment"
  @changePagination="changePagination"
)
.no-results(v-if="appointments != null && !appointments.length") 
  base-icon(name="exclamation") 
  span No matching records

HospitalAppointmentRecordOverlay(
  :key="`${componentKey}-1`",
  :isOpen="isHospitalAppointmentRecordOverlayOpen",
  tabType="requested"
  :showAppointment="showAppointment"
  @closeHospitalAppointmentRecordOverlay="closeHospitalAppointmentRecordOverlay",
  :specialtyModule="specialtyModule"
)
AppointmentNotesOverlay(
  :isOpen="isAppointmentNotesOverlayOpen",
  @closeAppointmentNotesOverlay="closeAppointmentNotesOverlay"
)
</template>

<script>
import { defineComponent } from "vue";
import TableTitle from "@/components/TableTitle.vue";
import DateSelector from "@/components/DateSelector.vue";
import Pagination from "@/components/Pagination.vue";
import listPagesMixin from "@/mixins/listPagesMixin";

import HospitalAppointmentRecordOverlay from "@/components/hospitals/appointment-status/overlays/HospitalAppointmentRecordOverlay.vue";
import AppointmentNotesOverlay from "@/components/overlays/side-overlays/AppointmentNotesOverlay.vue";

import { fetchPatientAppointment } from "@/api/patientAppointmentApi";

import { apiStatus, apiStatusComputedFactory } from "@/api";
const { IDLE, PENDING, SUCCESS, ERROR } = apiStatus;

import {
  getDateFromUnixFormatted,
  getTimeFromUnixFormatted,
  getRelativeTimeFromUnixFormatted,
  getUserFormatted,
  getDoctorFormatted,
} from "@/helpers";
import sortHospitalAppointments from "@/mixins/sortHospitalAppointments";

export default defineComponent({
  components: {
    TableTitle,
    DateSelector,
    Pagination,
    HospitalAppointmentRecordOverlay,
    AppointmentNotesOverlay,
  },
  emits: ["refreshAppointmentsSummary"],
  mixins: [listPagesMixin, sortHospitalAppointments],

  data() {
    return {
      searchKeyword: "",
      isHospitalAppointmentRecordOverlayOpen: false,
      isAppointmentNotesOverlayOpen: false,
      fetchPatientAppointmentStatus: IDLE,
      fetchPatientStatus: IDLE,
      appointments: null,
      showAppointment: null,
      date: null,
    };
  },
  computed: {
    ...apiStatusComputedFactory("fetchPatientAppointmentStatus"),
  },
  props: {
    specialtyModule: {
      required: false,
    },
  },
  methods: {
    getDateFromUnixFormatted,
    getTimeFromUnixFormatted,
    getRelativeTimeFromUnixFormatted,
    getUserFormatted,
    getDoctorFormatted,
    filterByDate(date) {
      this.date = date;
      this.fetchPatientAppointment();
    },
    async fetchPatientAppointment() {
      this.fetchPatientAppointmentStatus = PENDING;
      try {
        delete this.reqPagination.quantity;
        const order = this.listOrder
        ? {
            by: this.listOrder.by,
            direction: this.listOrder.direction,
          }
        : null;

        const res = await fetchPatientAppointment({
          params: {
            ...this.reqPagination,
            order,
            idHospital: this.$route.params.idHospital,
            include: [
              "clinic",
              "hospitalDoctor",
              "patient",
              "referringDoctor",
              "hospital",
              "cancelled",
            ],
            stage: "REQUESTED",
            scheduledAt: this.date ?? null,
            idSpecialtyModule: this.$route.params.idSpecialty,
            ...(this.searchKeyword
              ? { searchKeyword: this.searchKeyword }
              : {}),
          },
        });

        this.appointments = res.data.patientAppointments;
        this.pagination = res.data.pagination;
        this.fetchPatientAppointmentStatus = SUCCESS;
      } catch (err) {
        this.fetchPatientAppointmentStatus = ERROR;
        console.error(err);
      }
    },
    openHospitalAppointmentRecordOverlay(appointment) {
      this.componentKey = this.componentKey + 1;
      this.showAppointment = appointment;
      this.isHospitalAppointmentRecordOverlayOpen = true;
    },
    closeHospitalAppointmentRecordOverlay(shouldRefresh = true) {
      if (shouldRefresh) {
        this.$emit("refreshAppointmentsSummary");
        this.fetchPatientAppointment();
      }
      this.isHospitalAppointmentRecordOverlayOpen = false;
    },

    openAppointmentNotesOverlay() {
      this.isAppointmentNotesOverlayOpen = true;
    },
    closeAppointmentNotesOverlay() {
      this.isAppointmentNotesOverlayOpen = false;
    },
  },
  created() {
    if (this.$route.params.idHospital === null) return;
    this.fetchPatientAppointment();
  },
});
</script>

<style scoped lang="scss">
.tr {
  cursor: pointer;
}
.td {
  &:nth-child(1) {
    width: 15%;
  }
  &:nth-child(2) {
    width: 18%;
  }
  &:nth-child(3) {
    width: 19.9%;
  }
  &:nth-child(4) {
    width: 17%;
  }
  &:nth-child(5) {
    width: 19.4%;
  }
  &:nth-child(6) {
    width: 8.7%;
  }
}
</style>
